/*
 * <td-showcase> — HOME-A owned (td-showcase-collections section).
 * Desktop (>=1024px), mode "scroll": the section becomes a tall scroller with a
 * sticky stage; scroll progress steps through the collection list and swaps the
 * image. Uses window.TD.onScrollProgress as the scroll tick when available, and
 * its own rAF-throttled scroll listener otherwise. Progress is always computed
 * from the scroller's own rect, so it does not depend on the helper's exact range.
 * Mode "hover", reduced motion, or narrower screens: hover / focus / click switch.
 */
if (!customElements.get('td-showcase')) {
  class TdShowcase extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-sc-item]'));
      this.images = Array.from(this.querySelectorAll('[data-sc-image]'));
      this.cta = this.querySelector('[data-sc-cta]');
      this.ctaTitle = this.querySelector('[data-sc-cta-title]');
      this.list = this.querySelector('[data-sc-list]');
      this.scroller = this.querySelector('[data-sc-scroller]');
      this.count = this.items.length;
      this.active = -1;
      if (!this.count) return;

      this.mqDesktop = window.matchMedia('(min-width: 1024px)');
      this.mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.items.forEach((btn, i) => {
        btn.addEventListener('click', () => this.select(i));
        btn.addEventListener('focus', () => {
          if (!this.scrollEnabled()) this.setActive(i);
        });
        btn.addEventListener('mouseenter', () => {
          if (!this.scrollEnabled() && this.dataset.mode === 'hover') this.setActive(i);
        });
      });

      this.onChange = () => this.refreshMode();
      this.addMqListener(this.mqDesktop, this.onChange);
      this.addMqListener(this.mqReduce, this.onChange);

      this.onTick = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.tick();
        });
      };

      // Theme editor: selecting a block (the card <li>) shows it.
      this.onBlockSelect = (e) => {
        const card = e.target && e.target.closest ? e.target.closest('[data-sc-image]') : null;
        if (!card || !this.contains(card)) return;
        const i = Number(card.dataset.index);
        this.setActive(i);
        if (!this.mqDesktop.matches) {
          card.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'auto' });
        }
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      // Only run scroll work while the section is near the viewport (200px margin).
      this.near = !('IntersectionObserver' in window);
      if ('IntersectionObserver' in window) {
        this.nearIo = new IntersectionObserver(
          (entries) => {
            this.near = entries[0].isIntersecting;
            if (this.near) this.tick();
          },
          { rootMargin: '200px 0px' }
        );
        this.nearIo.observe(this);
      }

      this.setActive(0);
      this.refreshMode();
    }

    disconnectedCallback() {
      this.detachScroll();
      if (this.nearIo) this.nearIo.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      if (this.mqDesktop) {
        this.removeMqListener(this.mqDesktop, this.onChange);
        this.removeMqListener(this.mqReduce, this.onChange);
      }
    }

    addMqListener(mq, fn) {
      if (mq.addEventListener) mq.addEventListener('change', fn);
      else if (mq.addListener) mq.addListener(fn);
    }

    removeMqListener(mq, fn) {
      if (mq.removeEventListener) mq.removeEventListener('change', fn);
      else if (mq.removeListener) mq.removeListener(fn);
    }

    scrollEnabled() {
      return (
        this.dataset.mode === 'scroll' &&
        this.count > 1 &&
        this.scroller &&
        this.mqDesktop.matches &&
        !this.mqReduce.matches
      );
    }

    refreshMode() {
      const on = this.scrollEnabled();
      this.toggleAttribute('data-scroll-active', on);
      if (on) this.attachScroll();
      else this.detachScroll();
    }

    attachScroll() {
      if (this.scrollAttached) return;
      this.scrollAttached = true;
      const helper = window.TD && typeof window.TD.onScrollProgress === 'function';
      if (helper) {
        // The helper calls back while the element crosses the viewport; we use it
        // as a tick and compute our own sticky-range progress from the rect.
        try {
          this.unsubscribe = window.TD.onScrollProgress(this.scroller, () => this.tick());
        } catch (e) {
          this.unsubscribe = null;
        }
      }
      if (!this.unsubscribe) {
        window.addEventListener('scroll', this.onTick, { passive: true });
        this.ownListener = true;
      }
      window.addEventListener('resize', this.onTick, { passive: true });
      this.tick();
    }

    detachScroll() {
      if (!this.scrollAttached) return;
      this.scrollAttached = false;
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      this.unsubscribe = null;
      if (this.ownListener) window.removeEventListener('scroll', this.onTick);
      this.ownListener = false;
      window.removeEventListener('resize', this.onTick);
    }

    progress() {
      const rect = this.scroller.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      if (travel <= 0) return 0;
      return Math.min(1, Math.max(0, -rect.top / travel));
    }

    tick() {
      if (!this.scrollAttached || !this.near || !this.scrollEnabled()) return;
      const p = this.progress();
      const idx = Math.min(this.count - 1, Math.floor(p * this.count));
      this.setActive(idx);
    }

    select(i) {
      if (this.scrollEnabled()) {
        // Scroll to the step for this item; the scroll handler sets it active.
        const rect = this.scroller.getBoundingClientRect();
        const travel = rect.height - window.innerHeight;
        const top = window.scrollY + rect.top + (travel * (i + 0.5)) / this.count;
        window.scrollTo({ top, behavior: this.mqReduce.matches ? 'auto' : 'smooth' });
      }
      this.setActive(i);
    }

    setActive(i) {
      if (i === this.active || i < 0 || i >= this.count) return;
      this.active = i;
      this.items.forEach((el, n) => {
        const on = n === i;
        el.classList.toggle('is-active', on);
        if (on) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
      this.images.forEach((el, n) => el.classList.toggle('is-active', n === i));
      const btn = this.items[i];
      if (this.cta && btn) {
        if (btn.dataset.url) this.cta.setAttribute('href', btn.dataset.url);
        if (this.ctaTitle) this.ctaTitle.textContent = `: ${btn.dataset.title || btn.textContent.trim()}`;
      }
      if (this.list) this.list.style.setProperty('--td-sc-active', String(i));
    }
  }
  customElements.define('td-showcase', TdShowcase);
}
